import React, { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../style.css";
import { Form, Button, DatePicker, Table, Spin, Pagination, Select } from 'antd';
import API, {
  BOOKMAKER,
  STATISTIC,
  STATISTIC_AGGREGATE,
  STATISTIC_MONEY_FLOW,
  STATISTIC_MONEY_FLOW_CHART,
  GAME,
} from "../../../network/api";
import moment from "moment";
import "moment-timezone";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement } from 'chart.js';
import { getGame, getCategory, extractNumbers } from "../../../components/until";
import {
  EMPTY_DATA,
  TEXT_DEF,
} from "../../../constant/text";
import { RIGHTS } from "../../../constant/rights";
import { hasRight } from "../../../components/until/right";
import dayjs from  'dayjs';
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import { resolveBookmakerDisplayName } from "../../../utils/resolveBookmakerName";
import ReportStatisticTabs from "../../../components/ReportStatisticTabs";
import StatisticReportBarChart from "../../../components/StatisticReportBarChart";

const { MonthPicker } = DatePicker;

/** Route tiếng Anh cho màn «Thống kê tổng hợp». */
const REPORT_AGGREGATE_STATISTICS_PATH = "/report-aggregate-statistics";

const REPORT_STAT_TAB_KEYS = ["summary", "cashflow", "chart"];
const SUMMARY_BOOKMARKER_ALL = "all";
const CHART_RANGE_DAY = "day";
const CHART_RANGE_MONTH = "month";

/** API aggregate / money-flow: chỉ nhận `day` | `month`. */
const normalizeTableSearchBy = (v) => (v === "month" ? "month" : "day");

const labelAggregateFrom = (searchBy) =>
  searchBy === "month" ? "Từ tháng" : TEXT_DEF.text0024;
const labelAggregateTo = (searchBy) =>
  searchBy === "month" ? "Đến tháng" : TEXT_DEF.text0025;

/** Tối đa 3 tháng lịch giữa Từ tháng và Đến tháng (chênh `month` ≤ 2). */
const MAX_MONTH_SPAN_DIFF = 2;

/** Từ tháng: không gắn với Đến tháng; chỉ không chọn tháng sau tháng hiện tại. */
const disabledMonthFromAny = (current) =>
  Boolean(current && current > dayjs().endOf("month"));

/**
 * Sau khi đổi Từ tháng: giữ Đến tháng trong [Từ, Từ+2 tháng] và không sau tháng hiện tại.
 */
const clampMonthToToValidRange = (monthFromStr, monthToStr) => {
  const fromM = dayjs(monthFromStr, "MM-YYYY").startOf("month");
  if (!fromM.isValid()) return null;
  const todayMonth = dayjs().startOf("month");
  const maxBySpan = fromM.add(MAX_MONTH_SPAN_DIFF, "month");
  const maxAllowed = maxBySpan.isAfter(todayMonth, "month")
    ? todayMonth
    : maxBySpan;
  let toM = monthToStr
    ? dayjs(monthToStr, "MM-YYYY").startOf("month")
    : maxAllowed;
  if (toM.isBefore(fromM, "month")) toM = fromM;
  if (toM.isAfter(maxAllowed, "month")) toM = maxAllowed;
  return {
    monthTo: toM.format("MM-YYYY"),
    dateTo: toM.endOf("month").format("YYYY-MM-DD"),
  };
};

const disabledMonthToWithFromCap = (monthFromStr) => (current) => {
  const today = dayjs().endOf("month");
  if (!monthFromStr) {
    return current && current > today;
  }
  const minDate = dayjs(monthFromStr, "MM-YYYY");
  const maxDate = minDate.add(MAX_MONTH_SPAN_DIFF, "month").endOf("month");
  return current && (current < minDate || current > maxDate || current > today);
};

ChartJS.register(ArcElement, Tooltip, Legend, BarElement);

const getTotalPlayingUsersFromStatisticItem = (item) =>
  item?.totalPlayingUsers ??
  item?.playingUsers ??
  item?.playing_users ??
  item?.userPlaying ??
  item?.usersPlaying ??
  item?.countUserPlay ??
  item?.usersBetting ??
  item?.userBetting;

const getTotalDepositFromStatisticItem = (item) =>
  item?.totalDeposit ??
  item?.total_deposit ??
  item?.depositAmount ??
  item?.totalMoneyDeposit ??
  item?.moneyDeposit ??
  item?.walletIn ??
  item?.totalWalletIn ??
  item?.moneyIn ??
  item?.totalIn;

const getTotalWithdrawFromStatisticItem = (item) =>
  item?.totalWithdraw ??
  item?.total_withdraw ??
  item?.withdrawAmount ??
  item?.totalMoneyWithdraw ??
  item?.moneyWithdraw ??
  item?.walletOut ??
  item?.totalWalletOut ??
  item?.moneyOut ??
  item?.totalOut;

const formatStatisticTimeDisplay = (t) => {
  if (t == null || t === "") return "";
  const s = String(t).trim();
  const strict = moment(
    s,
    [
      "DD/MM/YYYY",
      "DD-MM-YYYY",
      "YYYY-MM-DD",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ssZ",
    ],
    true
  );
  if (strict.isValid()) return strict.format("DD/MM/YYYY");
  const loose = moment(s);
  return loose.isValid() ? loose.format("DD/MM/YYYY") : s;
};

const formatChartAxisLabel = (t) => {
  const s = formatStatisticTimeDisplay(t);
  if (!s) return String(t ?? "");
  const parts = s.split("/");
  if (parts.length >= 2) {
    const d = String(parts[0]).padStart(2, "0");
    const m = String(parts[1]).padStart(2, "0");
    return `${d}/${m}`;
  }
  return s;
};

/** Trục biểu đồ theo tháng: ưu tiên MM/YYYY; ngày giữ DD/MM như cũ. */
const formatChartAxisLabelForRow = (timeValue, searchBy) => {
  if (searchBy === "month") {
    const raw = String(timeValue ?? "").trim();
    const m = moment(
      raw,
      [
        "DD/MM/YYYY",
        "DD-MM-YYYY",
        "YYYY-MM-DD",
        "YYYY-MM-DD HH:mm:ss",
        "MM-YYYY",
        "MM/YYYY",
      ],
      true
    );
    if (m.isValid()) return m.format("MM/YYYY");
  }
  return formatChartAxisLabel(timeValue);
};

const computeChartRangeFromPreset = (preset) => {
  if (preset === CHART_RANGE_DAY || preset === "last5days") {
    const end = moment().endOf("day");
    const start = moment().subtract(4, "days").startOf("day");
    return {
      searchBy: "day",
      dateFrom: start.format("YYYY-MM-DD"),
      dateTo: end.format("YYYY-MM-DD"),
      monthFrom: start.format("MM-YYYY"),
      monthTo: end.format("MM-YYYY"),
      rangePreset: preset,
    };
  }
  if (preset === CHART_RANGE_MONTH || preset === "last3months") {
    const end = moment().endOf("month");
    const start = moment().subtract(2, "months").startOf("month");
    return {
      searchBy: "month",
      monthFrom: start.format("MM-YYYY"),
      monthTo: end.format("MM-YYYY"),
      dateFrom: start.clone().startOf("day").format("YYYY-MM-DD"),
      dateTo: end.clone().endOf("month").format("YYYY-MM-DD"),
      rangePreset: preset,
    };
  }
  return {};
};

const CHART_SLICE = {
  CASHFLOW: "cashflow",
  USERS: "users",
  BETS: "bets",
};

const createChartSlicesForBookmaker = (bm) => {
  const base = {
    ...computeChartRangeFromPreset(CHART_RANGE_DAY),
    bookmarker: bm,
    loading: false,
    rows: [],
  };
  return {
    [CHART_SLICE.CASHFLOW]: { ...base },
    [CHART_SLICE.USERS]: { ...base },
    [CHART_SLICE.BETS]: { ...base },
  };
};

/**
 * Chuẩn hóa phản hồi GET statistic/aggregate (`aggregateInfo`) sang dạng bảng giống statistic cũ.
 */
const mapAggregateInfoToDisplay = (aggregateInfo, bookmarkers, dollarUSFormatter) => {
  const list = Array.isArray(aggregateInfo) ? aggregateInfo : [];
  const finalResult = [];
  let sumUsers = 0;
  let sumNewUsers = 0;
  let sumOrders = 0;
  let sumBet = 0;
  let sumPaymentWin = 0;
  let sumProfit = 0;

  for (const item of list) {
    const profit = Number(item.totalProfit ?? item.bookmarkerProfit ?? 0);
    const totalBet = Number(item.totalBet ?? 0);
    const percentProfit = Number(((profit / (totalBet || 1)) * 100) || 0).toFixed(2);
    const paymentWinRaw = Number(item.totalPaymentWin ?? item.paymentWin ?? 0);
    const depositRaw = Number(getTotalDepositFromStatisticItem(item)) || 0;
    const withdrawRaw = Number(getTotalWithdrawFromStatisticItem(item)) || 0;
    const playingN =
      Number(getTotalPlayingUsersFromStatisticItem(item)) || 0;
    const orderCount = Number(item.totalOrders ?? item.count ?? 0);

    finalResult.push({
      profit: dollarUSFormatter.format(profit),
      percentProfit:
        (Number(percentProfit) <= 100 && Number(percentProfit) > 0) ||
        (Number(percentProfit) >= -100 && Number(percentProfit) < 0)
          ? percentProfit
          : Number(percentProfit) > 100
            ? 100
            : 0,
      bookmarkerProfit: item.bookmarkerProfit ?? profit,
      count: orderCount,
      newUsers: item.newUsers ?? 0,
      paymentWin: dollarUSFormatter.format(paymentWinRaw),
      time: item.time,
      totalBet: dollarUSFormatter.format(totalBet),
      totalUsers: item.totalUsers,
      totalPlayingUsers: playingN,
      totalDeposit: dollarUSFormatter.format(depositRaw),
      totalWithdraw: dollarUSFormatter.format(withdrawRaw),
      bookmarkerName: resolveBookmakerDisplayName(item, bookmarkers),
      totalBetRaw: totalBet,
      profitRaw: profit,
      totalDepositRaw: depositRaw,
      totalWithdrawRaw: withdrawRaw,
      paymentWinRaw,
      countRaw: orderCount,
    });

    sumUsers += Number(item.totalUsers) || 0;
    sumNewUsers += Number(item.newUsers) || 0;
    sumOrders += orderCount;
    sumBet += totalBet;
    sumPaymentWin += paymentWinRaw;
    sumProfit += profit;
  }

  const allPercentProfit =
    !sumProfit || !sumBet ? 0 : ((sumProfit / sumBet) * 100).toFixed(2);

  return {
    finalResult,
    finalInfo: {
      allUser: sumUsers,
      allNewUser: sumNewUsers,
      allOrders: sumOrders,
      allBet: sumBet,
      allAmountUserWin: sumPaymentWin,
      allProfit: sumProfit,
      allPercentProfit,
    },
  };
};

/**
 * Chuẩn hóa phản hồi GET statistic/money-flow (`moneyFlowInfo`) sang dạng bảng cashflow.
 */
const mapMoneyFlowInfoToDisplay = (
  moneyFlowInfo,
  bookmarkers,
  dollarUSFormatter,
  rootMeta = {}
) => {
  const list = Array.isArray(moneyFlowInfo) ? moneyFlowInfo : [];
  const totalPlayingUsersRoot =
    Number(rootMeta?.totalPlayingUsers ?? rootMeta?.playingUsers ?? 0) || 0;

  const finalResult = [];
  for (const item of list) {
    const depositRaw = Number(item?.totalDeposit ?? 0) || 0;
    const withdrawRaw = Number(item?.totalWithdraw ?? 0) || 0;
    const profitRaw = Number(item?.totalProfit ?? 0) || 0;
    const totalPlayingUsers =
      Number(
        item?.totalPlayingUsers ?? item?.playingUsers ?? totalPlayingUsersRoot
      ) || 0;

    finalResult.push({
      bookmarkerName: resolveBookmakerDisplayName(item, bookmarkers),
      time: item?.time,
      totalPlayingUsers,
      totalDeposit: dollarUSFormatter.format(depositRaw),
      totalWithdraw: dollarUSFormatter.format(withdrawRaw),
      profit: dollarUSFormatter.format(profitRaw),

      // fields phụ phục vụ chart/tooltip (nếu cần)
      totalDepositRaw: depositRaw,
      totalWithdrawRaw: withdrawRaw,
      profitRaw,
    });
  }

  return finalResult;
};

/**
 * Chuẩn hóa phản hồi GET statistic/money-flow (`moneyFlowInfo`) sang rows cho chart cashflow.
 */
const mapMoneyFlowInfoToChartRows = (moneyFlowInfo, bookmarkers, dollarUSFormatter) => {
  const list = Array.isArray(moneyFlowInfo) ? moneyFlowInfo : [];
  const finalResult = [];

  for (const item of list) {
    const depositRaw = Number(item?.totalDeposit ?? 0) || 0;
    const withdrawRaw = Number(item?.totalWithdraw ?? 0) || 0;
    const profitRaw = Number(item?.totalProfit ?? item?.profit ?? 0) || 0;

    finalResult.push({
      time: item?.time,
      bookmarkerName: resolveBookmakerDisplayName(item, bookmarkers),
      totalDepositRaw: depositRaw,
      totalWithdrawRaw: withdrawRaw,
      profitRaw,

      // các field formatted (không bắt buộc, nhưng tiện nếu component chart muốn show)
      totalDeposit: dollarUSFormatter.format(depositRaw),
      totalWithdraw: dollarUSFormatter.format(withdrawRaw),
      profit: dollarUSFormatter.format(profitRaw),
    });
  }

  return finalResult;
};

const buildZeroChartRowsFromSlice = (slice, sectionKey) => {
  if (!slice?.dateFrom || !slice?.dateTo) return [];
  const by = slice?.searchBy || "day";
  const start = moment(slice.dateFrom);
  const end = moment(slice.dateTo);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];

  const rows = [];
  if (by === "month") {
    const cursor = start.clone().startOf("month");
    const last = end.clone().endOf("month");
    while (cursor.isSameOrBefore(last, "month")) {
      rows.push({ time: cursor.format("DD/MM/YYYY") });
      cursor.add(1, "month");
    }
  } else {
    const cursor = start.clone().startOf("day");
    const last = end.clone().endOf("day");
    while (cursor.isSameOrBefore(last, "day")) {
      rows.push({ time: cursor.format("DD/MM/YYYY") });
      cursor.add(1, "day");
    }
  }

  return rows.map((r) => {
    if (sectionKey === CHART_SLICE.CASHFLOW) {
      return { ...r, totalDepositRaw: 0, totalWithdrawRaw: 0, profitRaw: 0 };
    }
    if (sectionKey === CHART_SLICE.USERS) {
      return { ...r, totalUsers: 0, totalPlayingUsers: 0 };
    }
    return { ...r, countRaw: 0, paymentWinRaw: 0, totalBetRaw: 0 };
  });
};

const extractMoneyFlowChartInfoArray = (respData) => {
  const d = respData ?? {};
  const sections = d?.sections ?? {};

  // Nhiều backend có thể trả về dưới các key khác nhau; cố gắng lấy cái array phù hợp nhất.
  const maybe =
    d?.moneyFlowStatsByTime ??
    d?.moneyFlowStats ??
    d?.rows ??
    d?.result ??
    sections?.moneyFlow?.moneyFlowInfo ??
    sections?.moneyFlow?.moneyFlowStatsByTime ??
    sections?.moneyFlow?.rows ??
    sections?.moneyFlow?.data ??
    sections?.moneyFlowInfo ??
    sections?.moneyFlow ??
    d?.moneyFlowInfo ??
    d?.money_flow_info ??
    d?.moneyFlowData ??
    d?.money_flow_data;

  return Array.isArray(maybe) ? maybe : [];
};

const extractTotalPlayingUsersChartInfoArray = (respData) => {
  const d = respData ?? {};
  const sections = d?.sections ?? {};

  const maybe =
    d?.totalPlayingUsersResult ??
    d?.playingUsersResult ??
    d?.totalPlayingUsersStatsByTime ??
    d?.playingUsersStatsByTime ??
    d?.totalPlayingUsersStats ??
    d?.playingUsersStats ??
    d?.rows ??
    d?.result ??
    sections?.totalPlayingUsers?.totalPlayingUsersStatsByTime ??
    sections?.totalPlayingUsers?.playingUsersStatsByTime ??
    sections?.playingUsers?.playingUsersStatsByTime ??
    sections?.totalPlayingUsers?.rows ??
    sections?.playingUsers?.rows ??
    sections?.totalPlayingUsers?.data ??
    sections?.playingUsers?.data ??
    sections?.totalPlayingUsersStatsByTime ??
    sections?.playingUsersStatsByTime ??
    sections?.totalPlayingUsers ??
    sections?.playingUsers ??
    d?.totalPlayingUsersInfo ??
    d?.playingUsersInfo ??
    d?.total_playing_users_info ??
    d?.playing_users_info;

  return Array.isArray(maybe) ? maybe : [];
};

const mapTotalPlayingUsersInfoToChartRows = (items) => {
  const list = Array.isArray(items) ? items : [];
  return list.map((item) => ({
    time: item?.time,
    totalUsers: Number(item?.totalUsers ?? item?.users ?? 0) || 0,
    totalPlayingUsers:
      Number(
        item?.totalPlayingUsers ??
          item?.playingUsers ??
          item?.userPlaying ??
          item?.usersPlaying ??
          0
      ) ||
      0,
  }));
};

const extractAllGamesBetChartInfoArray = (respData) => {
  const d = respData ?? {};
  const sections = d?.sections ?? {};

  const maybe =
    d?.allGamesBetResult ??
    d?.allGamesBetStatsByTime ??
    d?.allGamesBetStats ??
    d?.rows ??
    d?.result ??
    sections?.allGamesBet?.allGamesBetResult ??
    sections?.allGamesBet?.allGamesBetStatsByTime ??
    sections?.allGamesBet?.rows ??
    sections?.allGamesBet?.data ??
    sections?.allGamesBetResult ??
    sections?.allGamesBet;

  return Array.isArray(maybe) ? maybe : [];
};

const mapAllGamesBetInfoToChartRows = (items) => {
  const list = Array.isArray(items) ? items : [];
  return list.map((item) => ({
    time:
      item?.time ||
      (item?.groupKey ? moment(item.groupKey, "YYYY-MM-DD").format("DD/MM/YYYY") : ""),
    // Theo payload API allGamesBetStatsByTime
    countRaw: Number(item?.totalOrders ?? item?.orders ?? item?.count ?? item?.totalCount ?? 0) || 0,
    paymentWinRaw: Number(item?.totalPaymentWin ?? item?.paymentWin ?? item?.totalWin ?? 0) || 0,
    totalBetRaw: Number(item?.totalBet ?? item?.allGamesBet ?? item?.betAmount ?? item?.amountBet ?? 0) || 0,
    profitRaw: Number(item?.totalProfit ?? item?.profit ?? 0) || 0,
  }));
};

/** Dữ liệu demo khi API chưa trả dòng nào. `mode`: "day" (5 ngày) | "month" (3 tháng). */
const buildFakeChartOrdersInfo = (mode = "day") => {
  if (mode === "month") {
    const seeds = [
      {
        dep: 72000000000,
        wdr: 65500000000,
        prof: 6500000000,
        tu: 385000,
        pu: 128000,
        cnt: 245000,
        pw: 58500000000,
        tb: 70800000000,
      },
      {
        dep: 69800000000,
        wdr: 64200000000,
        prof: 5600000000,
        tu: 372000,
        pu: 121500,
        cnt: 238000,
        pw: 57100000000,
        tb: 68900000000,
      },
      {
        dep: 74500000000,
        wdr: 66800000000,
        prof: 7700000000,
        tu: 401000,
        pu: 135000,
        cnt: 251000,
        pw: 59800000000,
        tb: 71500000000,
      },
    ];
    return seeds.map((s, i) => {
      const m = moment().subtract(2 - i, "months").startOf("month");
      return {
        time: m.format("DD/MM/YYYY"),
        bookmarkerName: "Demo",
        totalDepositRaw: s.dep + i * 800000000,
        totalWithdrawRaw: s.wdr + i * 600000000,
        profitRaw: s.prof + i * 120000000,
        totalUsers: s.tu + i * 8000,
        totalPlayingUsers: s.pu + i * 2500,
        countRaw: s.cnt + i * 5000,
        paymentWinRaw: s.pw + i * 500000000,
        totalBetRaw: s.tb + i * 700000000,
      };
    });
  }

  const base = moment().startOf("day");
  const seeds = [
    { dep: 2450000000, wdr: 2230327000, prof: 219673000, tu: 12458, pu: 4544, cnt: 8120, pw: 1980000000, tb: 2400000000 },
    { dep: 2510000000, wdr: 2281000000, prof: 229000000, tu: 12880, pu: 4883, cnt: 8300, pw: 2010000000, tb: 2455000000 },
    { dep: 2380000000, wdr: 2195000000, prof: 185000000, tu: 12100, pu: 4201, cnt: 7950, pw: 1920000000, tb: 2310000000 },
    { dep: 2620000000, wdr: 2350000000, prof: 270000000, tu: 13200, pu: 5100, cnt: 8600, pw: 2080000000, tb: 2520000000 },
    { dep: 2490000000, wdr: 2268000000, prof: 222000000, tu: 12650, pu: 4720, cnt: 8210, pw: 1995000000, tb: 2420000000 },
  ];
  return seeds.map((s, i) => {
    const d = base.clone().subtract(4 - i, "days");
    return {
      time: d.format("DD/MM/YYYY"),
      bookmarkerName: "Demo",
      totalDepositRaw: s.dep + i * 12000000,
      totalWithdrawRaw: s.wdr + i * 8000000,
      profitRaw: s.prof + i * 3000000,
      totalUsers: s.tu + i * 120,
      totalPlayingUsers: s.pu + i * 40,
      countRaw: s.cnt + i * 15,
      paymentWinRaw: s.pw + i * 5000000,
      totalBetRaw: s.tb + i * 9000000,
    };
  });
};

const ReportStatisticTongHop = () => {
  const [summaryForm] = Form.useForm();
  const [cashFlowForm] = Form.useForm();
  const [chartFormCashflow] = Form.useForm();
  const [chartFormUsers] = Form.useForm();
  const [chartFormBets] = Form.useForm();
  const [chartSlices, setChartSlices] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const initialReportStatTab = (() => {
    const t = new URLSearchParams(location.search).get("statTab");
    return REPORT_STAT_TAB_KEYS.includes(t) ? t : "summary";
  })();
  const [reportStatTab, setReportStatTab] = useState(initialReportStatTab);
  const [dateFrom, setDateFrom] = useState(null);
  const [monthFrom, setMonthFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [monthTo, setMonthTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchBy, setSearchBy] = useState("day");
  const [gameTypes, setGameTypes] = useState();
  const [gameTypesReq, setGameTypesReq] = useState();
  const [bookmarkers, setBookmarkers] = useState();
  const [gameCategory, setGameCategory] = useState("");
  const [game, setGame] = useState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  /**
   * >0 sau khi đã bấm Tìm kiếm ít nhất một lần; tăng mỗi lần tìm để áp dụng filter mới.
   */
  const [tableSearchNonce, setTableSearchNonce] = useState(0);
  const [totalRecord, setTotalRecord] = useState(0);
  const [bookmarkerSelected, setBookmarkerSelected] = useState(null);
  const [ordersInfo, setOrdersInfo] = useState([]);
  const [finalInfo, setFinalInfo] = useState({
    allUser: 0,
    allNewUser: 0,
    allOrders: 0,
    allBet: 0,
    allAmountUserWin: 0,
    allProfit: 0,
    allPercentProfit: 0,
  });
  useEffect(() => {
    if (location.pathname !== REPORT_AGGREGATE_STATISTICS_PATH) return;
    const t = new URLSearchParams(location.search).get("statTab");
    if (REPORT_STAT_TAB_KEYS.includes(t)) {
      setReportStatTab(t);
    } else {
      setReportStatTab("summary");
    }
  }, [location.pathname, location.search]);

  const clearAggregateTableDisplay = () => {
    setLoading(false);
    setTableSearchNonce(0);
    setOrdersInfo([]);
    setFinalInfo({
      allUser: 0,
      allNewUser: 0,
      allOrders: 0,
      allBet: 0,
      allAmountUserWin: 0,
      allProfit: 0,
      allPercentProfit: 0,
    });
    setTotalRecord(0);
    setPage(1);
  };

  /** Xóa dữ liệu biểu đồ (rows) và đồng bộ form khi đổi tab — không giữ kết quả sau khi đã tìm kiếm. */
  const resetChartSlicesDisplay = () => {
    if (bookmarkerSelected == null || bookmarkerSelected === "") return;
    const slices = createChartSlicesForBookmaker(bookmarkerSelected);
    setChartSlices(slices);
    const fv = {
      rangePreset: CHART_RANGE_DAY,
      bookmarker: bookmarkerSelected,
    };
    chartFormCashflow.setFieldsValue(fv);
    chartFormUsers.setFieldsValue(fv);
    chartFormBets.setFieldsValue(fv);
  };

  const handleReportStatTabChange = (key) => {
    setReportStatTab(key);
    navigate(`${REPORT_AGGREGATE_STATISTICS_PATH}?statTab=${key}`, { replace: true });

    clearAggregateTableDisplay();
    resetChartSlicesDisplay();

    if (key === "summary" || key === "cashflow") {
      const sb = normalizeTableSearchBy(searchBy);
      setSearchBy(sb);
      if (!gameCategory) setGameCategory("all");
      if (!dateFrom || !dateTo || !monthFrom || !monthTo) {
        ensureSummaryDateRange(sb);
      }
    }
  };

  // Khởi tạo chartSlices lần đầu (trước khi getBookmarkers xong chartSlices có thể null).
  useEffect(() => {
    if (reportStatTab !== "chart") return;
    if (bookmarkerSelected == null || bookmarkerSelected === "") return;

    if (!chartSlices) {
      const slices = createChartSlicesForBookmaker(bookmarkerSelected);
      setChartSlices(slices);
      const fv = {
        rangePreset: CHART_RANGE_DAY,
        bookmarker: bookmarkerSelected,
      };
      chartFormCashflow.setFieldsValue(fv);
      chartFormUsers.setFieldsValue(fv);
      chartFormBets.setFieldsValue(fv);
    }
  }, [reportStatTab, bookmarkerSelected]);

  // useEffect(() => {
  //   form.setFieldValue('dateTo', dayjs())
  //   form.setFieldValue('dateFrom', dayjs().add(-3, 'month'));
  //   onSearch();
  // }, [])

  useEffect(() => {
    setGame(undefined);
    if (gameCategory === "lottery") {
      getGameTypes(0);
    } else if (gameCategory === "casino") {
      getGameTypes(1);
    } else {
      getGameTypes(-1);
    }
  }, [gameCategory]);

  useEffect(() => {
    getBookmarkers();
  }, []);

  const aggregateFiltersReady =
    Boolean(
      dateFrom &&
        dateTo &&
        monthFrom &&
        monthTo &&
        gameCategory &&
        bookmarkerSelected != null &&
        bookmarkerSelected !== ""
    );

  /** Phân trang / đổi tab bảng / tìm kiếm (nonce); không phụ thuộc ngày → không refetch khi chỉ đổi filter. */
  useEffect(() => {
    if (reportStatTab === "chart") return;
    if (!aggregateFiltersReady) return;
    if (tableSearchNonce === 0) return;
    getOrders(undefined, undefined, reportStatTab === "cashflow" ? "cashflow" : "summary");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    reportStatTab,
    tableSearchNonce,
    aggregateFiltersReady,
  ]);

  const getGameTypes = async (type = null) => {
    const result = await API.get(`${GAME}/all/${type}`);
    if (!result?.data?.data?.result || result?.data?.data?.result.length === 0) {
      setGameTypes([]);
      return [];
    }

    const gameTypesXSN = (result?.data?.data?.result || []).map((item) => {
      return {
        key: item?.id,
        value: item?.category === "xoso" ? `${item?.parrentType}-${extractNumbers(item?.type)}` : item?.parrentType,
        label: getGame(item?.parrentType, extractNumbers(item?.type)),
      };
    });
    const gameTypesXSTT = await getGameTypesXSTT();
    let allGames = gameTypesXSN;
    if (type !== 1) {
      allGames = [...gameTypesXSN, ...gameTypesXSTT];
    }
    setGameTypes(allGames);
    return allGames;
  }

  const getGameTypesXSTT = async () => {
    const result = await API.get(`${GAME}/xstt`);
    if (!result?.data?.data || result?.data?.data?.length === 0) {
      return [];
    }

    const games = result?.data?.data || [];
    const { gamesMT, gamesMB, gamesMN } = games.reduce((init, currentValue) => {
      if (currentValue.parrentType === 'xsmt') {
        init.gamesMT.push(currentValue);
      } else if (currentValue.parrentType === 'xsmn') {
        init.gamesMN.push(currentValue);
      } else {
        init.gamesMB.push(currentValue);
      }

      return init;
    }, { gamesMT: [], gamesMB: [], gamesMN: [] });
    const gamesFinal = gamesMB.concat(gamesMT).concat(gamesMN);

    return gamesFinal.map((item) => {
      return {
        key: item?.id,
        value: `${item.lotteryType}_${item.parrentType}_${item.type}`,
        label: item.textView.includes('XS') ? item.textView.replace('XS', 'Xổ Số') : item.textView,
      };
    });
  };

  const ensureSummaryDateRange = (by) => {
    const t = by || "day";
    if (t === "month") {
      // Mặc định 3 tháng lịch gần nhất (giống preset biểu đồ), không gộp Từ/Đến cùng một tháng
      // (trước đây cả hai = tháng hiện tại → API chỉ trả đúng một tháng, ví dụ chỉ tháng 4).
      const end = moment().endOf("month");
      const start = moment().subtract(2, "months").startOf("month");
      setMonthFrom(start.format("MM-YYYY"));
      setMonthTo(end.format("MM-YYYY"));
      setDateFrom(start.format("YYYY-MM-DD"));
      setDateTo(end.format("YYYY-MM-DD"));
    } else {
      const end = moment().endOf("day");
      const start = moment().subtract(30, "days").startOf("day");
      setDateFrom(start.format("YYYY-MM-DD"));
      setDateTo(end.format("YYYY-MM-DD"));
      setMonthFrom(start.format("MM-YYYY"));
      setMonthTo(end.format("MM-YYYY"));
    }
  };

  const getBookmarkers = async () => {
    const result = await API.get(`${BOOKMAKER}/all`);
    const bookmarkers = result.data?.data?.result[0] || [];
    if (!bookmarkers || bookmarkers.length === 0) return;

    const tempBookmarkers = bookmarkers?.map((item) => {
      return {
        key: item?.id,
        value: item?.id,
        label: item?.name,
      };
    });
    const statTabQ = new URLSearchParams(window.location.search).get("statTab");
    const isTongHop =
      typeof window !== "undefined" &&
      window.location.pathname === REPORT_AGGREGATE_STATISTICS_PATH;
    const initBm =
      isTongHop && hasRight([RIGHTS.Super])
        ? SUMMARY_BOOKMARKER_ALL
        : tempBookmarkers?.[0]?.value;

    flushSync(() => {
      setBookmarkers(tempBookmarkers);
      setGameCategory("all");
      setBookmarkerSelected(initBm);
      if (statTabQ === "chart") {
        setChartSlices(createChartSlicesForBookmaker(initBm));
      } else {
        setSearchBy("day");
        ensureSummaryDateRange("day");
      }
    });
    summaryForm.setFieldsValue({
      bookmarker: isTongHop && hasRight([RIGHTS.Super])
        ? SUMMARY_BOOKMARKER_ALL
        : tempBookmarkers?.[0]?.value,
      searchBy: "day",
    });
    cashFlowForm.setFieldsValue({
      bookmarker: isTongHop && hasRight([RIGHTS.Super])
        ? SUMMARY_BOOKMARKER_ALL
        : tempBookmarkers?.[0]?.value,
      searchBy: "day",
    });
    const chartFv = {
      bookmarker: isTongHop && hasRight([RIGHTS.Super])
        ? SUMMARY_BOOKMARKER_ALL
        : tempBookmarkers?.[0]?.value,
      rangePreset: CHART_RANGE_DAY,
    };
    chartFormCashflow.setFieldsValue(chartFv);
    chartFormUsers.setFieldsValue(chartFv);
    chartFormBets.setFieldsValue(chartFv);
    await getGameTypes(-1);
  };

  const getOrders = async (
    forcedSearchBy,
    forcedBm,
    reportType = "summary"
  ) => {
    try {
      if (reportType === "cashflow") {
        const params = getMoneyFlowParams(false, forcedSearchBy, forcedBm);
        if (!params) return;

        setLoading(true);
        const result = await API.get(`${STATISTIC_MONEY_FLOW}?${params}`);
        const moneyFlowInfo = result.data?.data?.moneyFlowInfo ?? [];
        const total = result.data?.data?.total || 0;
        const rootMeta = {
          totalUsers: result.data?.data?.totalUsers ?? 0,
          totalPlayingUsers:
            result.data?.data?.totalPlayingUsers ??
            result.data?.data?.playingUsers ??
            0,
        };

        setTotalRecord(total);
        const finalResult = mapMoneyFlowInfoToDisplay(
          moneyFlowInfo,
          bookmarkers,
          dollarUS,
          rootMeta
        );
        setOrdersInfo(finalResult);

        const sumProfit = (Array.isArray(moneyFlowInfo) ? moneyFlowInfo : []).reduce(
          (acc, item) => acc + (Number(item?.totalProfit ?? 0) || 0),
          0
        );
        setFinalInfo({
          allUser: rootMeta?.totalUsers ?? 0,
          allNewUser: 0,
          allOrders: 0,
          allBet: 0,
          allAmountUserWin: 0,
          allProfit: sumProfit,
          allPercentProfit: 0,
        });
        setLoading(false);
        return;
      }

      let gt = gameTypes;
      if (!gt?.length && gameCategory === "all") {
        gt = await getGameTypes(-1);
      }
      if (!gt?.length) return;

      const params = getParams(false, gt, forcedSearchBy, forcedBm, {
        chartQuery: false,
      });
      if (!params) return;

      setLoading(true);
      const result = await API.get(`${STATISTIC_AGGREGATE}?${params}`);
      const aggregateInfo =
        result.data?.data?.aggregateInfo ??
        result.data?.data?.ordersInfo ??
        [];
      const total = result.data?.data?.total || 0;
      setTotalRecord(total);

      const { finalResult, finalInfo: computedFinal } =
        mapAggregateInfoToDisplay(aggregateInfo, bookmarkers, dollarUS);
      const sumOrders = result.data?.data?.sumOrders;
      setFinalInfo(
        sumOrders && typeof sumOrders === "object"
          ? {
              allUser: sumOrders.totalUsers ?? computedFinal.allUser,
              allNewUser: sumOrders.totalNewUsers ?? computedFinal.allNewUser,
              allOrders: sumOrders.totalOrders ?? computedFinal.allOrders,
              allBet: sumOrders.totalBet ?? computedFinal.allBet,
              allAmountUserWin:
                sumOrders.totalPaymentWin ?? computedFinal.allAmountUserWin,
              allProfit: sumOrders.totalProfit ?? computedFinal.allProfit,
              allPercentProfit:
                sumOrders.totalPercentProfit ?? computedFinal.allPercentProfit,
            }
          : computedFinal
      );
      setOrdersInfo(finalResult);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  }

  const getParams = (
    isExport = false,
    gtOverride,
    searchByOverride,
    bookmarkOverride,
    opts = {}
  ) => {
    if (!dateFrom || !dateTo || !monthFrom || !monthTo || !gameCategory) return;
    const gt = gtOverride ?? gameTypes;
    if (!gt?.length) return;

    const sb = normalizeTableSearchBy(searchByOverride ?? searchBy);
    const bmIn =
      bookmarkOverride !== undefined ? bookmarkOverride : bookmarkerSelected;
    // API aggregate hỗ trợ "ALL" bằng bookmarkerId=0 (theo mẫu response).
    const bmRaw = bmIn === SUMMARY_BOOKMARKER_ALL ? 0 : bmIn;
    if (!hasRight([RIGHTS.Super]) && (bmRaw == null || bmRaw === "" || bmRaw === 0)) return;

    const parts = [];
    if (bmRaw != null && bmRaw !== "") parts.push(`bookmarkerId=${bmRaw}`);
    parts.push(`searchBy=${sb}`);
    if (dateFrom) {
      parts.push(`fromDate=${moment(dateFrom).startOf("day").unix()}`);
    }
    if (dateTo) {
      parts.push(`toDate=${moment(dateTo).endOf("day").unix()}`);
    }
    if (game && game?.length > 0) {
      parts.push(`gameType=${game}`);
    }
    if (gameCategory) {
      parts.push(`game=${gameCategory}`);
      if (!game || game?.length === 0) {
        const dataGame = gt.map((obj) => obj.value).join(",");
        setGameTypesReq(dataGame);
        parts.push(`gameType=${dataGame}`);
      }
    }
    if (opts.chartQuery) {
      parts.push("page=1&limit=500");
    } else {
      parts.push(
        !isExport ? `page=${page}&limit=${pageSize}` : `page=1&limit=1000000`
      );
    }
    return parts.join("&");
  };

  /**
   * Params riêng cho `statistic/money-flow`:
   * - `fromDate/toDate` phải là `YYYY-MM-DD`
   * - Không truyền `game/gameType` để tránh URL dài và đúng yêu cầu backend cho cashflow.
   */
  const getMoneyFlowParams = (
    isExport = false,
    searchByOverride,
    bookmarkOverride
  ) => {
    if (!dateFrom || !dateTo) return;

    const sb = normalizeTableSearchBy(searchByOverride ?? searchBy);
    const bmIn =
      bookmarkOverride !== undefined ? bookmarkOverride : bookmarkerSelected;

    const bmRaw = bmIn === SUMMARY_BOOKMARKER_ALL ? 0 : bmIn;
    if (!hasRight([RIGHTS.Super]) && (bmRaw == null || bmRaw === "" || bmRaw === 0)) {
      return;
    }

    const parts = [];
    if (bmRaw != null && bmRaw !== "") parts.push(`bookmarkerId=${bmRaw}`);
    parts.push(`searchBy=${sb}`);

    parts.push(`fromDate=${moment(dateFrom).format("YYYY-MM-DD")}`);
    parts.push(`toDate=${moment(dateTo).format("YYYY-MM-DD")}`);

    parts.push(
      !isExport ? `page=${page}&limit=${pageSize}` : `page=1&limit=1000000`
    );

    return parts.join("&");
  };

  const dollarUS = Intl.NumberFormat("en-US");

  const mapOrdersInfoToChartRows = (ordersInfo) => {
    const finalResult = [];
    for (const item of ordersInfo) {
      const profit = Number(item.bookmarkerProfit);
      const percentProfit = Number(
        ((profit / Number(item.totalBet || 0)) * 100) || 0
      ).toFixed(2);
      const depositRaw = Number(getTotalDepositFromStatisticItem(item)) || 0;
      const withdrawRaw = Number(getTotalWithdrawFromStatisticItem(item)) || 0;
      const paymentWinRaw = Number(item.paymentWin) || 0;
      const playingN = Number(getTotalPlayingUsersFromStatisticItem(item)) || 0;
      finalResult.push({
        profit: dollarUS.format(Number(profit)),
        percentProfit:
          (Number(percentProfit) <= 100 && Number(percentProfit) > 0) ||
          (Number(percentProfit) >= -100 && Number(percentProfit) < 0)
            ? percentProfit
            : Number(percentProfit) > 100
              ? 100
              : 0,
        bookmarkerProfit: item.bookmarkerProfit,
        count: item.count,
        newUsers: item.newUsers,
        paymentWin: dollarUS.format(paymentWinRaw),
        time: item.time,
        totalBet: dollarUS.format(Number(item.totalBet)),
        totalUsers: item.totalUsers,
        totalPlayingUsers: playingN,
        totalDeposit: dollarUS.format(depositRaw),
        totalWithdraw: dollarUS.format(withdrawRaw),
        bookmarkerName: resolveBookmakerDisplayName(item, bookmarkers),
        totalBetRaw: Number(item.totalBet) || 0,
        profitRaw: profit,
        totalDepositRaw: depositRaw,
        totalWithdrawRaw: withdrawRaw,
        paymentWinRaw,
        countRaw: Number(item.count) || 0,
      });
    }
    return finalResult;
  };

  const buildMoneyFlowChartQueryString = (slice, section = "moneyFlow") => {
    if (!slice?.dateFrom || !slice?.dateTo || !slice?.searchBy) return null;
    if (slice?.bookmarker == null || slice?.bookmarker === "") return null;

    const bmRaw = slice.bookmarker === SUMMARY_BOOKMARKER_ALL ? 0 : slice.bookmarker;
    if (!hasRight([RIGHTS.Super]) && (bmRaw == null || bmRaw === "" || bmRaw === 0)) {
      return null;
    }

    const fromDate = slice.dateFrom ? moment(slice.dateFrom).format("YYYY-MM-DD") : "";
    const toDate = slice.dateTo ? moment(slice.dateTo).format("YYYY-MM-DD") : "";

    if (!fromDate || !toDate) return null;

    const parts = [
      `sections=${section}`,
      `bookmarkerId=${bmRaw}`,
      `searchBy=${slice.searchBy}`,
      `fromDate=${fromDate}`,
      `toDate=${toDate}`,
      "page=1&limit=500",
    ];
    return parts.join("&");
  };

  const handleChartSliceSearch = async (
    sectionKey,
    providedSlices = null,
    opts = {}
  ) => {
    const { skipValidate = false } = opts;
    const formMap = {
      [CHART_SLICE.CASHFLOW]: chartFormCashflow,
      [CHART_SLICE.USERS]: chartFormUsers,
      [CHART_SLICE.BETS]: chartFormBets,
    };

    if (!skipValidate) {
      try {
        await formMap[sectionKey].validateFields();
      } catch {
        setChartSlices((p) => ({
          ...p,
          [sectionKey]: { ...(p?.[sectionKey] || {}), loading: false },
        }));
        return;
      }
    }

    const activeSlices = providedSlices ?? chartSlices;
    const slice = activeSlices?.[sectionKey];
    if (!slice?.dateFrom || !slice?.dateTo) return;

    setChartSlices((prev) => {
      if (!prev?.[sectionKey]) return prev;
      return {
        ...prev,
        [sectionKey]: { ...prev[sectionKey], loading: true },
      };
    });

    const sectionParam =
      sectionKey === CHART_SLICE.CASHFLOW
        ? "moneyFlow"
        : sectionKey === CHART_SLICE.USERS
          ? "totalPlayingUsers"
          : "allGamesBet";

    const params = buildMoneyFlowChartQueryString(slice, sectionParam);
    if (!params) {
      setChartSlices((p) => ({
        ...p,
        [sectionKey]: { ...p[sectionKey], loading: false },
      }));
      return;
    }

    try {
      const result = await API.get(`${STATISTIC_MONEY_FLOW_CHART}?${params}`);
      let rows = [];
      if (sectionKey === CHART_SLICE.CASHFLOW) {
        const data = result?.data?.data ?? {};
        const infoArr = extractMoneyFlowChartInfoArray(data);
        rows = mapMoneyFlowInfoToChartRows(infoArr, bookmarkers, dollarUS);
      } else if (sectionKey === CHART_SLICE.USERS) {
        const data = result?.data?.data ?? {};
        const infoArr = extractTotalPlayingUsersChartInfoArray(data);
        rows = mapTotalPlayingUsersInfoToChartRows(infoArr);
      } else {
        const data = result?.data?.data ?? {};
        const infoArr = extractAllGamesBetChartInfoArray(data);
        rows = mapAllGamesBetInfoToChartRows(infoArr);
      }
      setChartSlices((p) => ({
        ...p,
        [sectionKey]: { ...p[sectionKey], rows, loading: false },
      }));
    } catch {
      setChartSlices((p) => ({
        ...p,
        [sectionKey]: { ...p[sectionKey], loading: false },
      }));
    }
  };

  const onChartSlicePresetChange = (sectionKey, preset) => {
    const r = computeChartRangeFromPreset(preset);
    setChartSlices((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], ...r },
    }));
    const formMap = {
      [CHART_SLICE.CASHFLOW]: chartFormCashflow,
      [CHART_SLICE.USERS]: chartFormUsers,
      [CHART_SLICE.BETS]: chartFormBets,
    };
    formMap[sectionKey].setFieldsValue({ rangePreset: preset });
  };

  const updateChartSliceDate = (sectionKey, e, type) => {
    if (e) {
      const d = dayjs(
        typeof e.toDate === "function"
          ? e.toDate()
          : e?.$d instanceof Date
            ? e.$d
            : e
      );
      if (!d.isValid()) return;
      setChartSlices((prev) => {
        const slice = prev[sectionKey];
        if (!slice) return prev;
        const next = { ...slice };
        const sb = slice.searchBy || "day";
        if (sb === "month") {
          if (type === "from") {
            next.monthFrom = d.format("MM-YYYY");
            next.dateFrom = d.startOf("month").format("YYYY-MM-DD");
            const clamped = clampMonthToToValidRange(
              next.monthFrom,
              next.monthTo
            );
            if (clamped) {
              next.monthTo = clamped.monthTo;
              next.dateTo = clamped.dateTo;
            }
          } else {
            next.monthTo = d.format("MM-YYYY");
            next.dateTo = d.endOf("month").format("YYYY-MM-DD");
          }
        } else if (type === "from") {
          next.dateFrom = d.format("YYYY-MM-DD");
          next.monthFrom = d.format("MM-YYYY");
        } else {
          next.dateTo = d.format("YYYY-MM-DD");
          next.monthTo = d.format("MM-YYYY");
        }
        return { ...prev, [sectionKey]: next };
      });
    } else {
      setChartSlices((prev) => {
        const slice = prev[sectionKey];
        if (!slice) return prev;
        const next = { ...slice };
        if (type === "from") {
          next.dateFrom = null;
          next.monthFrom = null;
        } else {
          next.dateTo = null;
          next.monthTo = null;
        }
        return { ...prev, [sectionKey]: next };
      });
    }
  };

  const disabledChartSliceDateFrom = (sectionKey) => (current) => {
    const slice = chartSlices?.[sectionKey];
    const dateToVal = slice?.dateTo;
    const sb = slice?.searchBy || "day";
    const isAfterEndOfDay = (c) => c && c > dayjs().endOf("day");
    const isBeforeStartOfDateTo = (c, months) =>
      c < dayjs(dateToVal).subtract(months, "months").startOf("day");
    const isAfterEndOfDateTo = (c) => c > dayjs(dateToVal).endOf("day");
    if (!current) return false;
    const monthsToSubtract = sb === "day" ? 3 : 2;
    if (!dateToVal) {
      return isAfterEndOfDay(current);
    }
    return (
      isAfterEndOfDay(current) ||
      isBeforeStartOfDateTo(current, monthsToSubtract) ||
      isAfterEndOfDateTo(current)
    );
  };

  const disabledChartSliceDateTo = (sectionKey) => (current) => {
    const slice = chartSlices?.[sectionKey];
    const dateFromVal = slice?.dateFrom;
    const sb = slice?.searchBy || "day";
    const isAfterEndOfDay = (c) => c && c > dayjs().endOf("day");
    const isBeforeStartOfDateFrom = (c) =>
      c < dayjs(dateFromVal).startOf("day");
    const isAfterEndOfDateFrom = (c, months) =>
      c > dayjs(dateFromVal).add(months, "months").endOf("day");
    if (!current) return false;
    const monthsToAdd = sb === "day" ? 3 : 2;
    if (!dateFromVal) {
      return isAfterEndOfDay(current);
    }
    return (
      isAfterEndOfDay(current) ||
      isBeforeStartOfDateFrom(current) ||
      isAfterEndOfDateFrom(current, monthsToAdd)
    );
  };

  const disabledChartSliceMonthFrom = (_sectionKey) => (current) =>
    disabledMonthFromAny(current);

  const disabledChartSliceMonthTo = (sectionKey) => (current) =>
    disabledMonthToWithFromCap(chartSlices?.[sectionKey]?.monthFrom)(current);

  const chartSectionMeta = [
    {
      key: CHART_SLICE.CASHFLOW,
      form: chartFormCashflow,
      title: "Biểu đồ Dòng tiền theo nhà điều hành",
    },
    {
      key: CHART_SLICE.USERS,
      form: chartFormUsers,
      title: "Biểu đồ Hoạt động người dùng",
    },
    {
      key: CHART_SLICE.BETS,
      form: chartFormBets,
      title: "Biểu đồ Chỉ số cược",
    },
  ];

  const renderChartSectionToolbar = (sectionKey, formInst) => {
    const bmOptions = hasRight([RIGHTS.Super])
      ? [
          { value: SUMMARY_BOOKMARKER_ALL, label: "Tất cả" },
          ...(bookmarkers || []),
        ]
      : bookmarkers || [];
    const slice = chartSlices?.[sectionKey];
    if (!slice) return null;

    return (
      <div className="col-12" style={{ marginBottom: 16 }}>
        <Form
          form={formInst}
          layout="inline"
          autoComplete="off"
          onFinish={() => handleChartSliceSearch(sectionKey)}
        >
          <Form.Item
            name="bookmarker"
            label="Nhà điều hành"
            rules={[{ required: true, message: "Vui lòng chọn nhà điều hành" }]}
          >
            <Select
              showSearch
              placeholder="Nhà điều hành"
              style={{ minWidth: 200 }}
              optionFilterProp="label"
              options={bmOptions}
              onChange={(val) => {
                setChartSlices((prev) => ({
                  ...prev,
                  [sectionKey]: { ...prev[sectionKey], bookmarker: val },
                }));
              }}
            />
          </Form.Item>
          <Form.Item
            name="rangePreset"
            label="Loại tổng hợp"
            rules={[{ required: true, message: "Vui lòng chọn" }]}
          >
            <Select
              style={{ minWidth: 220 }}
              onChange={(p) => onChartSlicePresetChange(sectionKey, p)}
              options={[
                { value: CHART_RANGE_DAY, label: TEXT_DEF.text0089 },
                {
                  value: CHART_RANGE_MONTH,
                  label: TEXT_DEF.text0090,
                },
              ]}
            />
          </Form.Item>
          {slice.searchBy === "day" ? (
            <>
              <Form.Item label={labelAggregateFrom("day")}>
                <DatePicker
                  format="DD-MM-YYYY"
                  style={{ width: 200 }}
                  value={
                    slice.dateFrom
                      ? dayjs(slice.dateFrom)
                      : null
                  }
                  onChange={(ev) => updateChartSliceDate(sectionKey, ev, "from")}
                  disabledDate={disabledChartSliceDateFrom(sectionKey)}
                />
              </Form.Item>
              <Form.Item label={labelAggregateTo("day")}>
                <DatePicker
                  format="DD-MM-YYYY"
                  style={{ width: 200 }}
                  value={
                    slice.dateTo
                      ? dayjs(slice.dateTo)
                      : null
                  }
                  onChange={(ev) => updateChartSliceDate(sectionKey, ev, "to")}
                  disabledDate={disabledChartSliceDateTo(sectionKey)}
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item label={labelAggregateFrom("month")}>
                <MonthPicker
                  format="MM-YYYY"
                  style={{ width: 160 }}
                  value={
                    slice.monthFrom
                      ? dayjs(slice.monthFrom, "MM-YYYY")
                      : null
                  }
                  onChange={(ev) => updateChartSliceDate(sectionKey, ev, "from")}
                  disabledDate={disabledChartSliceMonthFrom(sectionKey)}
                />
              </Form.Item>
              <Form.Item label={labelAggregateTo("month")}>
                <MonthPicker
                  format="MM-YYYY"
                  style={{ width: 160 }}
                  value={
                    slice.monthTo ? dayjs(slice.monthTo, "MM-YYYY") : null
                  }
                  onChange={(ev) => updateChartSliceDate(sectionKey, ev, "to")}
                  disabledDate={disabledChartSliceMonthTo(sectionKey)}
                />
              </Form.Item>
            </>
          )}
          <Form.Item>
            <Button htmlType="submit" type="primary">
              {TEXT_DEF.text0026}
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  };

  const summaryColumns = [
    {
      title: "Nhà điều hành",
      dataIndex: "bookmarkerName",
      key: "bookmarkerName",
    },
    {
      title: "Thời gian",
      key: "time",
      render: (_, record) => formatStatisticTimeDisplay(record.time),
    },
    {
      title: "User đăng ký",
      key: "totalUsers",
      render: (_, record) => dollarUS.format(Number(record.totalUsers) || 0),
    },
    {
      title: "User chơi",
      key: "totalPlayingUsers",
      render: (_, record) =>
        dollarUS.format(Number(record.totalPlayingUsers) || 0),
    },
    {
      title: "Đơn cược",
      key: "count",
      render: (_, record) => dollarUS.format(Number(record.count) || 0),
    },
    {
      title: "Tiền khách thắng",
      dataIndex: "paymentWin",
      key: "paymentWin",
    },
    {
      title: "Tiền lợi nhuận",
      dataIndex: "profit",
      key: "profit",
    },
  ];

  const cashflowColumns = [
    {
      title: "Nhà điều hành",
      dataIndex: "bookmarkerName",
      key: "bookmarkerName",
    },
    {
      title: "Thời gian",
      key: "time",
      render: (_, record) => formatStatisticTimeDisplay(record.time),
    },
    {
      title: "User chơi",
      key: "totalPlayingUsers",
      render: (_, record) =>
        dollarUS.format(Number(record.totalPlayingUsers) || 0),
    },
    {
      title: "Tổng tiền nạp",
      dataIndex: "totalDeposit",
      key: "totalDeposit",
    },
    {
      title: "Tổng tiền rút",
      dataIndex: "totalWithdraw",
      key: "totalWithdraw",
    },
    {
      title: "Tiền lợi nhuận",
      dataIndex: "profit",
      key: "profit",
    },
  ];

  const runMinimalStatisticSearch = async (values) => {
    let gt = gameTypes;
    if (!gt?.length && gameCategory === "all") {
      gt = await getGameTypes(-1);
    }
    if (!gt?.length) return;

    const bm =
      values.bookmarker === SUMMARY_BOOKMARKER_ALL
        ? SUMMARY_BOOKMARKER_ALL
        : values.bookmarker;
    const sb = normalizeTableSearchBy(values.searchBy);

    flushSync(() => {
      setPage(1);
      setGame(undefined);
      setGameCategory("all");
      setBookmarkerSelected(bm);
      setSearchBy(sb);
      // Không ghi đè range nếu người dùng đã chọn Từ/Đến ở DatePicker.
      const needRange =
        sb === "day"
          ? !dateFrom || !dateTo
          : sb === "month"
            ? !monthFrom || !monthTo || !dateFrom || !dateTo
            : true;
      if (needRange) ensureSummaryDateRange(sb);
      setTableSearchNonce((n) => n + 1);
    });
    summaryForm.setFieldsValue({ bookmarker: bm, searchBy: sb });
    cashFlowForm.setFieldsValue({ bookmarker: bm, searchBy: sb });
  };

  const handleSummarySearch = async () => {
    const v = await summaryForm.validateFields();
    await runMinimalStatisticSearch(v);
  };

  const handleCashFlowSearch = async () => {
    const v = await cashFlowForm.validateFields();
    await runMinimalStatisticSearch(v);
  };

  const renderSummarySearch = () => {
    const bmOptions = hasRight([RIGHTS.Super])
      ? [
          { value: SUMMARY_BOOKMARKER_ALL, label: "Tất cả" },
          ...(bookmarkers || []),
        ]
      : bookmarkers || [];

    return (
      <div className="col-12" style={{ marginBottom: 16 }}>
        <Form
          form={summaryForm}
          layout="inline"
          autoComplete="off"
          onFinish={handleSummarySearch}
        >
          <Form.Item
            name="bookmarker"
            label="Nhà điều hành"
            rules={[{ required: true, message: "Vui lòng chọn nhà điều hành" }]}
          >
            <Select
              showSearch
              placeholder="Nhà điều hành"
              style={{ minWidth: 220 }}
              optionFilterProp="label"
              options={bmOptions}
              onChange={(val) => setBookmarkerSelected(val)}
            />
          </Form.Item>
          <Form.Item
            name="searchBy"
            label="Loại tổng hợp"
            rules={[{ required: true, message: "Vui lòng chọn loại tổng hợp" }]}
          >
            <Select
              placeholder="Loại tổng hợp"
              style={{ minWidth: 200 }}
              onChange={(val) => {
                const next = normalizeTableSearchBy(val);
                setSearchBy(next);
                ensureSummaryDateRange(next);
              }}
            >
              <Select.Option value="day">{TEXT_DEF.text0089}</Select.Option>
              <Select.Option value="month">{TEXT_DEF.text0090}</Select.Option>
            </Select>
          </Form.Item>
          {(() => {
            const currentBy = normalizeTableSearchBy(searchBy);
            if (currentBy === "day") {
              return (
                <>
                  <Form.Item label={labelAggregateFrom(currentBy)}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      style={{ width: 200 }}
                      value={
                        dateFrom
                          ? dayjs(dateFrom)
                          : null
                      }
                      onChange={(ev) => {
                        if (!ev) return;
                        const dt =
                          ev?.$d ??
                          (typeof ev?.toDate === "function"
                            ? ev.toDate()
                            : ev);
                        const m = moment(dt);
                        setDateFrom(m.format("YYYY-MM-DD"));
                        setMonthFrom(m.format("MM-YYYY"));
                      }}
                    />
                  </Form.Item>
                  <Form.Item label={labelAggregateTo(currentBy)}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      style={{ width: 200 }}
                      value={
                        dateTo ? dayjs(dateTo) : null
                      }
                      onChange={(ev) => {
                        if (!ev) return;
                        const dt =
                          ev?.$d ??
                          (typeof ev?.toDate === "function"
                            ? ev.toDate()
                            : ev);
                        const m = moment(dt);
                        setDateTo(m.format("YYYY-MM-DD"));
                        setMonthTo(m.format("MM-YYYY"));
                      }}
                    />
                  </Form.Item>
                </>
              );
            }

            return (
              <>
                <Form.Item label={labelAggregateFrom(currentBy)}>
                  <MonthPicker
                    format="MM-YYYY"
                    style={{ width: 160 }}
                    value={
                      monthFrom ? dayjs(monthFrom, "MM-YYYY") : null
                    }
                    onChange={(ev) => {
                      if (!ev) return;
                      const mf = ev.format("MM-YYYY");
                      setMonthFrom(mf);
                      setDateFrom(ev.startOf("month").format("YYYY-MM-DD"));
                      const clamped = clampMonthToToValidRange(mf, monthTo);
                      if (clamped) {
                        setMonthTo(clamped.monthTo);
                        setDateTo(clamped.dateTo);
                      }
                    }}
                    disabledDate={disabledMonthFromAny}
                  />
                </Form.Item>
                <Form.Item label={labelAggregateTo(currentBy)}>
                  <MonthPicker
                    format="MM-YYYY"
                    style={{ width: 160 }}
                    value={monthTo ? dayjs(monthTo, "MM-YYYY") : null}
                    onChange={(ev) => {
                      if (!ev) return;
                      setMonthTo(ev.format("MM-YYYY"));
                      setDateTo(ev.endOf("month").format("YYYY-MM-DD"));
                    }}
                    disabledDate={disabledMonthToWithFromCap(monthFrom)}
                  />
                </Form.Item>
              </>
            );
          })()}
          <Form.Item>
            <Button htmlType="submit" type="primary">
              {TEXT_DEF.text0026}
            </Button>
          </Form.Item>
          {hasRight([RIGHTS.ExportExcelReportOrdersByBookmarker]) ? (
            <Form.Item>
              <Button
                htmlType="button"
                type="primary"
                style={{ marginLeft: 20 }}
                onClick={onExportExl}
              >
                {TEXT_DEF.text0027}
              </Button>
            </Form.Item>
          ) : null}
        </Form>
      </div>
    );
  };

  const renderCashFlowSearch = () => {
    const bmOptions = hasRight([RIGHTS.Super])
      ? [
          { value: SUMMARY_BOOKMARKER_ALL, label: "Tất cả" },
          ...(bookmarkers || []),
        ]
      : bookmarkers || [];

    return (
      <div className="col-12" style={{ marginBottom: 16 }}>
        <Form
          form={cashFlowForm}
          layout="inline"
          autoComplete="off"
          onFinish={handleCashFlowSearch}
        >
          <Form.Item
            name="bookmarker"
            label="Nhà điều hành"
            rules={[{ required: true, message: "Vui lòng chọn nhà điều hành" }]}
          >
            <Select
              showSearch
              placeholder="Nhà điều hành"
              style={{ minWidth: 220 }}
              optionFilterProp="label"
              options={bmOptions}
              onChange={(val) => setBookmarkerSelected(val)}
            />
          </Form.Item>
          <Form.Item
            name="searchBy"
            label="Loại tổng hợp"
            rules={[{ required: true, message: "Vui lòng chọn loại tổng hợp" }]}
          >
            <Select
              placeholder="Loại tổng hợp"
              style={{ minWidth: 200 }}
              onChange={(val) => {
                const next = normalizeTableSearchBy(val);
                setSearchBy(next);
                ensureSummaryDateRange(next);
              }}
            >
              <Select.Option value="day">{TEXT_DEF.text0089}</Select.Option>
              <Select.Option value="month">{TEXT_DEF.text0090}</Select.Option>
            </Select>
          </Form.Item>
          {(() => {
            const currentBy = normalizeTableSearchBy(searchBy);
            if (currentBy === "day") {
              return (
                <>
                  <Form.Item label={labelAggregateFrom(currentBy)}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      style={{ width: 200 }}
                      value={
                        dateFrom
                          ? dayjs(dateFrom)
                          : null
                      }
                      onChange={(ev) => {
                        if (!ev) return;
                        const dt =
                          ev?.$d ??
                          (typeof ev?.toDate === "function"
                            ? ev.toDate()
                            : ev);
                        const m = moment(dt);
                        setDateFrom(m.format("YYYY-MM-DD"));
                        setMonthFrom(m.format("MM-YYYY"));
                      }}
                    />
                  </Form.Item>
                  <Form.Item label={labelAggregateTo(currentBy)}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      style={{ width: 200 }}
                      value={
                        dateTo ? dayjs(dateTo) : null
                      }
                      onChange={(ev) => {
                        if (!ev) return;
                        const dt =
                          ev?.$d ??
                          (typeof ev?.toDate === "function"
                            ? ev.toDate()
                            : ev);
                        const m = moment(dt);
                        setDateTo(m.format("YYYY-MM-DD"));
                        setMonthTo(m.format("MM-YYYY"));
                      }}
                    />
                  </Form.Item>
                </>
              );
            }

            return (
              <>
                <Form.Item label={labelAggregateFrom(currentBy)}>
                  <MonthPicker
                    format="MM-YYYY"
                    style={{ width: 160 }}
                    value={
                      monthFrom ? dayjs(monthFrom, "MM-YYYY") : null
                    }
                    onChange={(ev) => {
                      if (!ev) return;
                      const mf = ev.format("MM-YYYY");
                      setMonthFrom(mf);
                      setDateFrom(
                        ev.startOf("month").format("YYYY-MM-DD")
                      );
                      const clamped = clampMonthToToValidRange(mf, monthTo);
                      if (clamped) {
                        setMonthTo(clamped.monthTo);
                        setDateTo(clamped.dateTo);
                      }
                    }}
                    disabledDate={disabledMonthFromAny}
                  />
                </Form.Item>
                <Form.Item label={labelAggregateTo(currentBy)}>
                  <MonthPicker
                    format="MM-YYYY"
                    style={{ width: 160 }}
                    value={monthTo ? dayjs(monthTo, "MM-YYYY") : null}
                    onChange={(ev) => {
                      if (!ev) return;
                      setMonthTo(ev.format("MM-YYYY"));
                      setDateTo(
                        ev.endOf("month").format("YYYY-MM-DD")
                      );
                    }}
                    disabledDate={disabledMonthToWithFromCap(monthFrom)}
                  />
                </Form.Item>
              </>
            );
          })()}
          <Form.Item>
            <Button htmlType="submit" type="primary">
              {TEXT_DEF.text0026}
            </Button>
          </Form.Item>
          {hasRight([RIGHTS.ExportExcelReportOrdersByBookmarker]) ? (
            <Form.Item>
              <Button
                htmlType="button"
                type="primary"
                style={{ marginLeft: 20 }}
                onClick={onExportExl}
              >
                {TEXT_DEF.text0027}
              </Button>
            </Form.Item>
          ) : null}
        </Form>
      </div>
    );
  };

  const onExportExl = async () => {
    try {
      let gt = gameTypes;
      if (!gt?.length && gameCategory === "all") {
        gt = await getGameTypes(-1);
      }
      if (!gt?.length) return;

      const excelVariant =
        reportStatTab === "summary"
          ? "summary"
          : reportStatTab === "cashflow"
            ? "cashflow"
            : "full";

      const params =
        excelVariant === "cashflow"
          ? getMoneyFlowParams(true)
          : getParams(true, gt);
      if (!params) return;

      setLoading(true);
      const finalResult = [];

      if (excelVariant === "cashflow") {
        const result = await API.get(`${STATISTIC_MONEY_FLOW}?${params}`);
        const moneyFlowInfo = result.data?.data?.moneyFlowInfo ?? [];
        const total = result.data?.data?.total || 0;
        const totalPlayingUsersRoot = Number(
          result.data?.data?.totalPlayingUsers ??
            result.data?.data?.playingUsers ??
            0
        );

        setTotalRecord(total);

        for (const item of moneyFlowInfo) {
          finalResult.push({
            bookmarkerName: resolveBookmakerDisplayName(item, bookmarkers),
            time: item.time,
            totalPlayingUsers:
              Number(
                item.totalPlayingUsers ??
                  item.playingUsers ??
                  totalPlayingUsersRoot
              ) || 0,
            totalDeposit: Number(item.totalDeposit ?? 0) || 0,
            totalWithdraw: Number(item.totalWithdraw ?? 0) || 0,
            profit: Number(item.totalProfit ?? 0) || 0,
          });
        }
      } else {
        const result = await API.get(`${STATISTIC_AGGREGATE}?${params}`);
        const aggregateInfo =
          result.data?.data?.aggregateInfo ?? result.data?.data?.ordersInfo ?? [];
        const total = result.data?.data?.total || 0;

        setTotalRecord(total);

        for (const item of aggregateInfo) {
          const profit = Number(item.totalProfit ?? item.bookmarkerProfit ?? 0);
          const totalBet = Number(item.totalBet ?? 0);
          const percentProfit = Number(((profit / (totalBet || 1)) * 100) || 0).toFixed(2);
          const orderCount = Number(item.totalOrders ?? item.count ?? 0);
          const paymentWin = Number(item.totalPaymentWin ?? item.paymentWin ?? 0);

          finalResult.push({
            bookmarkerName: resolveBookmakerDisplayName(item, bookmarkers),
            time: item.time,
            totalUsers: item.totalUsers,
            totalPlayingUsers:
              getTotalPlayingUsersFromStatisticItem(item) ?? 0,
            newUsers: item.newUsers ?? 0,
            count: orderCount,
            totalBet,
            paymentWin,
            profit,
            totalDeposit: Number(getTotalDepositFromStatisticItem(item)) || 0,
            totalWithdraw: Number(getTotalWithdrawFromStatisticItem(item)) || 0,
            percentProfit:
              (Number(percentProfit) <= 100 && Number(percentProfit) > 0) ||
              (Number(percentProfit) >= -100 && Number(percentProfit) < 0)
                ? percentProfit
                : Number(percentProfit) > 100
                  ? 100
                  : 0,
          });
        }
      }

      exportToExcel(finalResult, excelVariant);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const exportToExcel = (data, variant = "full") => {
    const name = "Thống kê tổng hợp";
    let formattedData;
    let columnFormats;

    if (variant === "summary") {
      formattedData = data.map((item) => ({
        "Nhà điều hành": item.bookmarkerName,
        "Thời gian": formatStatisticTimeDisplay(item.time),
        "User đăng ký": item.totalUsers,
        "User chơi":
          item.totalPlayingUsers ??
          getTotalPlayingUsersFromStatisticItem(item) ??
          0,
        "Đơn cược": item.count,
        "Tiền khách thắng": item.paymentWin,
        "Tiền lợi nhuận": item.profit,
      }));
      columnFormats = [
        { header: "Nhà điều hành", key: "Nhà điều hành" },
        { header: "Thời gian", key: "Thời gian" },
        { header: "User đăng ký", key: "User đăng ký", type: "n" },
        { header: "User chơi", key: "User chơi", type: "n" },
        { header: "Đơn cược", key: "Đơn cược", type: "n" },
        { header: "Tiền khách thắng", key: "Tiền khách thắng", type: "n" },
        { header: "Tiền lợi nhuận", key: "Tiền lợi nhuận", type: "n" },
      ];
    } else if (variant === "cashflow") {
      formattedData = data.map((item) => ({
        "Nhà điều hành": item.bookmarkerName,
        "Thời gian": formatStatisticTimeDisplay(item.time),
        "User chơi":
          item.totalPlayingUsers ??
          getTotalPlayingUsersFromStatisticItem(item) ??
          0,
        "Tổng tiền nạp": item.totalDeposit,
        "Tổng tiền rút": item.totalWithdraw,
        "Tiền lợi nhuận": item.profit,
      }));
      columnFormats = [
        { header: "Nhà điều hành", key: "Nhà điều hành" },
        { header: "Thời gian", key: "Thời gian" },
        { header: "User chơi", key: "User chơi", type: "n" },
        { header: "Tổng tiền nạp", key: "Tổng tiền nạp", type: "n" },
        { header: "Tổng tiền rút", key: "Tổng tiền rút", type: "n" },
        { header: "Tiền lợi nhuận", key: "Tiền lợi nhuận", type: "n" },
      ];
    } else {
      formattedData = data.map((item) => ({
        "Nhà điều hành": item.bookmarkerName,
        "Thời gian": item.time,
        "User đăng ký": item.totalUsers,
        "User mới": item.newUsers,
        "Đơn cược": item.count,
        "Tiền cược": item.totalBet,
        "Tiền khách thắng": item.paymentWin,
        "Lợi nhuận": item.profit,
        "% thắng": item.percentProfit,
      }));
      columnFormats = [
        { header: "Nhà điều hành", key: "Nhà điều hành" },
        { header: "Thời gian", key: "Thời gian" },
        { header: "User đăng ký", key: "User đăng ký", type: "n" },
        { header: "User mới", key: "User mới", type: "n" },
        { header: "Đơn cược", key: "Đơn cược", type: "n" },
        { header: "Tiền cược", key: "Tiền cược", type: "n" },
        { header: "Tiền khách thắng", key: "Tiền khách thắng", type: "n" },
        { header: "Lợi nhuận", key: "Lợi nhuận", type: "n" },
        { header: "% thắng", key: "% thắng", type: "n" },
      ];
    }

    const ws = XLSX.utils.json_to_sheet(formattedData);

    formattedData.forEach((row, rowIndex) => {
      columnFormats.forEach((col, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ c: colIndex, r: rowIndex + 1 });
        if (col.type === "n" && ws[cellRef]) {
          ws[cellRef].t = "n";
        }
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `${name}.xlsx`);
  };

  const onChangePage = (page, size) => {
    setPage(page);
    setPageSize(size)
  };


  const showTotal = (total) => `Tổng cộng ${total} dữ liệu`;

  const renderTableData = (opts = {}) => {
    const { hideAggregateStrip, columns: columnsProp, scrollX: scrollXProp } =
      opts;
    const tableColumns = columnsProp ?? [];
    return (
      <Spin spinning={loading} delay={500} size="large">
        {!hideAggregateStrip ? (
          <div
            style={{
              display: "flex",
              marginTop: 5,
              marginRight: 20,
            }}
          >
            <div className="text-lg font-bold mr-5">
              <label>User đăng kýs: </label> <span>{dollarUS.format(finalInfo.allUser)}</span>
            </div>
            <div className="text-lg font-bold mr-5">
              <label>User mới: </label> <span>{dollarUS.format(finalInfo.allNewUser)}</span>
            </div>
            <div className="text-lg font-bold mr-5">
              <label>Tổng đơn cược: </label> <span>{dollarUS.format(finalInfo.allOrders)}</span>
            </div>
            <div className="text-lg font-bold mr-5">
              <label>Tổng tiền cược: </label> <span>{dollarUS.format(finalInfo.allBet)}</span>
            </div>
            <div className="text-lg font-bold mr-5">
              <label>Tổng tiền khách thắng: </label> <span>{dollarUS.format(finalInfo.allAmountUserWin)}</span>
            </div>
            <div className="text-lg font-bold mr-5">
              <label>Tổng lợi nhuận: </label> <span>{dollarUS.format(finalInfo.allProfit)}</span>
            </div>
            <div className="text-lg font-bold mr-5">
              <label>Phần trăm lợi nhuận: </label> <span>{finalInfo.allPercentProfit}</span>
            </div>
          </div>
        ) : null}
        <div style={{ display: "flex", width: "100%", marginTop: 20 }}>
          <Table
            key="userList"
            locale={EMPTY_DATA}
            columns={tableColumns}
            dataSource={ordersInfo}
            pagination={false}
            bordered
            style={{ width: "100%" }}
            scroll={{
              x: scrollXProp ?? (columnsProp ? 980 : 1200),
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            marginTop: 15,
            justifyContent: "flex-end",
          }}
        >
          <div>
            <Pagination 
              current={page} 
              total={totalRecord} 
              onChange={onChangePage} 
              showTotal={showTotal} 
              showSizeChanger
              onShowSizeChange={onChangePage}
              pageSizeOptions={['10', '50', '100', '200', '500', '1000']}
            />
          </div>
        </div>
      </Spin>
    );
  }

  const renderChartDashboard = () => {
    if (!chartSlices) {
      return (
        <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
          Đang khởi tạo…
        </div>
      );
    }

    const rowsForSlice = (sectionKey) => {
      const slice = chartSlices[sectionKey];
      const rowsRaw = Array.isArray(slice?.rows) ? slice.rows : [];
      const rows =
        rowsRaw.length > 0
          ? rowsRaw
          : buildZeroChartRowsFromSlice(slice, sectionKey);
      return {
        rows,
        loading: !!slice?.loading,
        labels: rows.map((r) =>
          formatChartAxisLabelForRow(r.time, slice?.searchBy)
        ),
      };
    };

    const datasetsForKey = (key, rows) => {
      if (key === CHART_SLICE.CASHFLOW) {
        return [
          {
            label: "Tổng tiền nạp",
            data: rows.map((r) => r.totalDepositRaw ?? 0),
            backgroundColor: "rgba(59, 130, 246, 0.9)",
          },
          {
            label: "Tổng tiền rút",
            data: rows.map((r) => r.totalWithdrawRaw ?? 0),
            backgroundColor: "rgba(34, 197, 94, 0.9)",
          },
          {
            label: "Lợi nhuận",
            data: rows.map((r) => r.profitRaw ?? 0),
            backgroundColor: "rgba(249, 115, 22, 0.9)",
          },
        ];
      }
      if (key === CHART_SLICE.USERS) {
        return [
          {
            label: "User đăng ký",
            data: rows.map((r) => Number(r.totalUsers) || 0),
            backgroundColor: "rgba(59, 130, 246, 0.9)",
          },
          {
            label: "User chơi",
            data: rows.map((r) => Number(r.totalPlayingUsers) || 0),
            backgroundColor: "rgba(34, 197, 94, 0.9)",
          },
        ];
      }
      return [
        {
          label: "Số đơn cược",
          data: rows.map((r) => r.countRaw ?? 0),
          backgroundColor: "rgba(244, 114, 182, 0.9)",
        },
        {
          label: "Tiền khách thắng",
          data: rows.map((r) => r.paymentWinRaw ?? 0),
          backgroundColor: "rgba(234, 179, 8, 0.9)",
        },
        {
          label: "Tổng tiền cược",
          data: rows.map((r) => r.totalBetRaw ?? 0),
          backgroundColor: "rgba(34, 197, 94, 0.9)",
        },
      ];
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 48,
          marginTop: 8,
        }}
      >
        {chartSectionMeta.map(({ key, form, title }) => {
          const { rows, loading, labels } = rowsForSlice(key);
          return (
            <section key={key}>
              <h3
                style={{
                  margin: "0 0 16px",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                {title}
              </h3>
              {renderChartSectionToolbar(key, form)}
              <Spin spinning={loading && rows.length === 0} delay={200} size="large">
                <StatisticReportBarChart
                  labels={labels}
                  legendPosition="bottom"
                  showDataLabels
                  datasets={datasetsForKey(key, rows)}
                />
              </Spin>
            </section>
          );
        })}
      </div>
    );
  };

  return (
    <div className="col-12">
      <ReportStatisticTabs
        activeKey={reportStatTab}
        onChange={handleReportStatTabChange}
      />
      {reportStatTab === "summary" && renderSummarySearch()}
      {reportStatTab === "cashflow" && renderCashFlowSearch()}
      {(reportStatTab === "summary" || reportStatTab === "cashflow") &&
        renderTableData({
          hideAggregateStrip: true,
          columns:
            reportStatTab === "summary"
              ? summaryColumns
              : cashflowColumns,
          scrollX: reportStatTab === "summary" ? 980 : 860,
        })}
      {reportStatTab === "chart" && renderChartDashboard()}
    </div>
  );
};

export default ReportStatisticTongHop;
